// Utility helpers dedicated to normalizing promotion frequency payloads
// so that every consumer (Vue forms, tests, etc.) works with predictable values.

// Small helper to normalise day-of-week data from the API into an array of strings.
function normalizeDaysOfWeek(daysOfWeek) {
    if (!daysOfWeek) {
        return []
    }

    if (Array.isArray(daysOfWeek)) {
        return daysOfWeek.map((day) => String(day).trim()).filter(Boolean)
    }

    if (typeof daysOfWeek === "string") {
        return daysOfWeek
            .split(",")
            .map((day) => day.trim())
            .filter(Boolean)
    }

    return []
}

// Helper to coerce SQL time strings (`09:00:00`) into HTML friendly values (`09:00`).
function normalizeTimeValue(timeValue) {
    if (!timeValue) {
        return null
    }

    if (typeof timeValue !== "string") {
        return timeValue
    }

    if (timeValue.length >= 5) {
        return timeValue.slice(0, 5)
    }

    return timeValue
}

// Ensure we always hand HTML inputs a simple `YYYY-MM-DD` string.
function normalizeDateOnly(value) {
    if (!value) {
        return null
    }

    if (typeof value === "string") {
        // API date-only and zone-less SQL values are calendar dates, not instants.
        // Parsing local midnight as Date shifts them a day for operators east of UTC.
        const calendarDate = value.match(/^(\d{4}-\d{2}-\d{2})(?:[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?)?$/)
        if (calendarDate) return calendarDate[1]
    }

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
        return null
    }

    return parsed.toISOString().slice(0, 10)
}

// Single frequency normaliser used by both the component and the unit tests.
function normalizeFrequency(frequency) {
    const normalised = { ...frequency }

    normalised.startTime = normalizeTimeValue(frequency.startTime)
    normalised.endTime = normalizeTimeValue(frequency.endTime)
    normalised.specificDate = normalizeDateOnly(frequency.specificDate)
    normalised.fromDate = normalizeDateOnly(frequency.fromDate)
    normalised.toDate = normalizeDateOnly(frequency.toDate)
    normalised.daysOfWeek = normalizeDaysOfWeek(frequency.daysOfWeek)
    normalised.recurrenceStartDateEnabled = Boolean(frequency.recurrenceStartDateEnabled)
    normalised.recurrenceEndDateEnabled = Boolean(frequency.recurrenceEndDateEnabled)

    if (!normalised.recurrenceType) {
        normalised.recurrenceType = "week"
    }

    return normalised
}

// Exported API – always work with arrays to keep the component tidy.
function normalizeFrequenciesFromApi(frequencies = []) {
    return frequencies.map((frequency) => normalizeFrequency(frequency))
}

export {
    normalizeFrequenciesFromApi,
    normalizeFrequency,
    normalizeTimeValue,
    normalizeDateOnly,
    normalizeDaysOfWeek,
}

